import { useQuery, useMutation, useQueries } from '@tanstack/react-query';

import {
  getApprovalStatus,
  getApprovalDocList,
  removeApprovalDoc,
  postApprovalSign,
  postApprovalReviewerDone,
  getPcApprovalDocList,
  executeDocumentsState,
  referenceDocumentState,
  sendExecutionCompleteMail,
  getApprovalSignImage,
} from '@entities/approval/api/approval';

/**
 * 문서함 상태창 / 결재함 상태창 정보 요청
 */
function useGetApprovalStatusQuery(options = {}) {
  return useQuery({
    queryKey: ['approvalStatus'],
    queryFn: getApprovalStatus,
    // 결재 상태 카운트는 변동이 있을 수 있어 1분간만 캐싱
    staleTime: 1000 * 60 * 1,
    ...options,
  });
}

/**
 * 문서함 요청
 */
function useGetApprovalDocListQuery(type, params = {}, options = {}) {
  return useQuery({
    queryKey: ['approvalList', type, params],
    queryFn: () => getApprovalDocList({ type, ...params }),
    enabled: !!type,
    // 결재 목록은 실시간성이 중요하므로 1분간만 캐싱
    staleTime: 1000 * 60 * 1,
    ...options,
  });
}
/**
 * 문서 (반려) 삭제
 */
function useRemoveApprovalDocMutation(options = {}) {
  return useMutation({
    mutationFn(idx) {
      return removeApprovalDoc(idx);
    },
    ...options,
  });
}

/**
 * 결재자 결재 처리
 */
function usePostApprovalSignMutation(options = {}) {
  return useMutation({
    mutationFn({ signNumber, idx, comment }) {
      return postApprovalSign({ signNumber, idx, comment });
    },
    ...options,
  });
}

/**
 * 검토자 검토 처리
 */
function usePostApprovalReviewerDoneMutation(options = {}) {
  return useMutation({
    mutationFn({ checkNumber, idx, comment }) {
      return postApprovalReviewerDone({ checkNumber, idx, comment });
    },
    ...options,
  });
}

/**
 * 문서함 요청
 */
function useGetPcApprovalDocListQuery(type, params = {}, options = {}) {
  return useQuery({
    queryKey: ['pcApprovalList', type, params],
    queryFn: () => getPcApprovalDocList({ type, ...params }),

    enabled: options.enabled ?? !!type,
    // LNB·탭 전환 시 캐시로 인한 생략을 막기 위해 항상 stale로 두고 마운트/활성화 시 재조회
    staleTime: 0,
    refetchOnMount: 'always',
    // 백그라운드 주기 갱신(2분) — 메뉴/탭 재진입은 위 옵션으로 최신화
    refetchInterval: 1000 * 60 * 2,
    refetchOnWindowFocus: false, // 창 포커스 시 refetch 비활성 (전역 설정 상속)

    refetchIntervalInBackground: false,
    ...options,
  });
}

/**
 * 시행 완료 상태 변경
 */
function useExecuteDocumentsStateMutation(options = {}) {
  /**
   * 시행완료 상태변경 mutation 훅
   */
  return useMutation({
    mutationFn: (selectedIds) => executeDocumentsState(selectedIds),
    ...options,
  });
}
/**
 * 참조 완료 상태 변경
 */
function useReferenceDocumentStateMutation(options = {}) {
  /**
   * 참조완료 상태변경 mutation 훅
   */
  return useMutation({
    mutationFn: (selectedIds) => referenceDocumentState(selectedIds),
    ...options,
  });
}

/**
 * 시행완료 메일발송 (단건)
 */
function useSendExecutionCompleteMailMutation(options = {}) {
  /**
   * 시행함 완료 문서 메일발송 mutation 훅
   * @param {number} docIdx - 발송 대상 문서 ID (단건)
   */
  return useMutation({
    mutationFn: (docIdx) => sendExecutionCompleteMail(docIdx),
    ...options,
  });
}

/**
 * 서명 이미지 다중 조회 (useQueries 사용)
 * @param {number} docIdx - 문서 인덱스
 * @param {Array} signers - 서명자 목록 [{ key: 'sign_1', enumber: 12345 }, ...]
 * @param {Object} options - React Query 옵션
 * @returns {Object} - { data: { sign_1: base64Image, ... }, isLoading, isError }
 */
function useSignImageQueries(docIdx, signers = [], options = {}) {
  const queries = useQueries({
    queries: signers.map(function (signer) {
      return {
        queryKey: ['signImage', docIdx, signer.key],
        queryFn() {
          return getApprovalSignImage(docIdx, signer.key);
        },
        enabled: !!docIdx && signers.length > 0,
        // 서명 이미지는 자주 변경되지 않으므로 5분간 캐싱
        staleTime: 1000 * 60 * 5,
        // 실패 시 재시도 하지 않음 (서명이 없을 수 있음)
        retry: false,
        ...options,
      };
    }),
  });

  // 결과를 { sign_1: base64Image, sign_2: base64Image, ... } 형태로 변환
  const data = {};
  const isLoading = queries.some(function (q) {
    return q.isLoading;
  });
  const isError = queries.every(function (q) {
    return q.isError;
  });

  queries.forEach(function (query, index) {
    if (query.data?.resultData?.signImage) {
      const signer = signers[index];
      data[signer.key] = query.data.resultData.signImage;
    }
  });

  return { data, isLoading, isError };
}

export {
  useGetApprovalStatusQuery,
  useGetApprovalDocListQuery,
  useRemoveApprovalDocMutation,
  usePostApprovalSignMutation,
  usePostApprovalReviewerDoneMutation,
  useGetPcApprovalDocListQuery,
  useExecuteDocumentsStateMutation,
  useReferenceDocumentStateMutation,
  useSendExecutionCompleteMailMutation,
  useSignImageQueries,
};
